import axios from "axios";

/**
 * Frappe API root (no trailing slash); paths append `/method/...`.
 * Uses env-driven ERP base so BK can switch between UAT/Prod.
 */
const API_BASE = (
  import.meta.env.DEV
    ? "/api"
    : import.meta.env.VITE_ERP_API_BASE).replace(/\/+$/, "");


// Guest/open APIs: no cookies or auth headers needed.
const erpApi = axios.create();

const METHOD_PATH =
  "/method/bengaitlabserp.cloudflare.list_r2_files.get_sales_order_full_order_and_files";

/**
 * Frappe often returns HTTP 200 with `message: { status: 'error', message: '...', status_code: 400 }`.
 * Axios treats that as success; throw so callers can use try/catch and show error toasts.
 * @param {unknown} data - Axios response `data`
 */
export function throwIfFrappeMessageError(data) {
  const inner = data?.message;
  if (inner && typeof inner === "object" && inner.status === "error") {
    const text =
      typeof inner.message === "string"
        ? inner.message
        : typeof inner.exc === "string"
          ? inner.exc
          : "Request failed";
    const err = new Error(text);
    err.frappeStatus = inner.status;
    err.frappeStatusCode = inner.status_code;
    err.responseData = data;
    throw err;
  }
}

/**
 * Message string for toast/UI from axios errors or {@link throwIfFrappeMessageError} throws.
 * @param {unknown} err
 */
export function getBkErrorMessage(err) {
  if (!err) return "Request failed";
  const d = err?.response?.data;
  let m = d?.message;
  if (
    m &&
    typeof m === "object" &&
    m.status === "error" &&
    typeof m.message === "string"
  ) {
    return m.message;
  }
  if (typeof m === "string") return m;
  if (typeof d?.exc === "string") return d.exc;
  if (typeof err?.message === "string") return err.message;
  return "Request failed";
}

export async function fetchOrderData(salesOrder, customer) {
  const { data } = await erpApi.post(
    `${API_BASE}${METHOD_PATH}`,
    { sales_order: salesOrder, customer },
    { headers: { "Content-Type": "application/json" } },
  );
  return data?.message ?? data;
}

/**
 * Upload locking checkpoint with optional `variant`: 'plain' | 'bulldog'.
 * Backend keeps legacy behavior when variant is omitted/empty.
 */
export async function uploadLockingCheckpointVariant(
  salesOrderId,
  customer,
  fileOrFiles,
  variant,
  metadata,
  contentSha256,
) {
  void contentSha256;
  return uploadBkDesignStepCheckpoint({
    salesOrderId,
    customer,
    step: "locking",
    mode: "stl",
    variant: variant != null && variant !== "" ? String(variant) : "plain",
    file: fileOrFiles,
    metadata,
  });
}

function formatSignedValue(val) {
  if (val == null || val === "") return "0";
  const num = Number(val);
  if (isNaN(num)) return "0";
  if (num === 0) return "0";
  return num >= 0 ? `+${num}` : String(num);
}

/** CPO value is "present" when API sent a real number (0 is valid). Missing → fall back to default. */
function hasCpoInputMm(raw) {
  return raw !== null && raw !== undefined && raw !== "";
}

/** Socket design row: prefer cpo_input_mm when set (including 0), else default_mm */
function resolveSocketDesignDisplayMm(entry) {
  if (hasCpoInputMm(entry.cpo_input_mm)) {
    return formatSignedValue(entry.cpo_input_mm);
  }
  return formatSignedValue(entry.default_mm);
}

/** Format scalars from ERP/Frappe (numbers, strings, nulls) for display */
export function formatApiScalar(val) {
  if (val === null || val === undefined) return "-";
  if (val === "") return "-";
  if (typeof val === "number") {
    if (!Number.isFinite(val)) return "-";
    const rounded = Math.round(val * 1000) / 1000;
    if (
      Number.isInteger(rounded) ||
      Math.abs(rounded - Math.round(rounded)) < 1e-6
    ) {
      return String(Math.round(rounded));
    }
    return String(rounded);
  }
  return String(val);
}

function firstDefined(...values) {
  for (const v of values) {
    if (v !== null && v !== undefined && v !== "") return v;
  }
  return null;
}

function pick(obj, ...keys) {
  if (!obj) return null;
  for (const key of keys) {
    const v = obj?.[key];
    if (v !== null && v !== undefined && v !== "") return v;
  }
  return null;
}

/**
 * Raw value from value_c_details row whose gap parses to targetCm (e.g. "03 cm" → 3).
 */
function getValueCAtGapRaw(orderData, targetCm) {
  const details = orderData.value_c_details;
  if (!Array.isArray(details)) return null;
  for (const row of details) {
    const gap = String(row?.gap ?? "");
    const m = gap.match(/(\d+)/);
    if (!m) continue;
    const n = parseInt(m[1], 10);
    if (n === targetCm) {
      const v = row?.value;
      if (v === null || v === undefined || v === "") return null;
      return v;
    }
  }
  return null;
}

/**
 * Primary Value C: exact 5 cm row if present; else ERP uses 03/06/… (no 05) — try 3 then 0; then extra fields.
 */
function resolveValueCPrimaryMm(orderData) {
  const tryOrder = [5, 3, 0];
  for (const cm of tryOrder) {
    const raw = getValueCAtGapRaw(orderData, cm);
    if (raw !== null) return formatApiScalar(raw);
  }
  const alt =
    orderData.reduction_circum_5cm ??
    orderData.circumference_at_5cm ??
    orderData.circumference_5cm ??
    orderData.stump_circumference_5cm;
  if (alt != null && String(alt).trim() !== "") return formatApiScalar(alt);
  return "-";
}

/** Sorted rows for UI: { gap, value } as formatted strings */
function mapValueCircumferenceDetails(orderData) {
  const details = orderData.value_c_details;
  if (!Array.isArray(details)) return [];
  return [...details]
    .sort((a, b) => (a.idx ?? 0) - (b.idx ?? 0))
    .map((row) => ({
      gap: row.gap != null ? String(row.gap) : "—",
      value: formatApiScalar(row.value),
    }));
}

export function mapOrderToPatientData(data) {
  const od = data.order_data || {};
  const foot = od.foot_details || od.foot || {};

  /** Demographics & contact — not socket/assessment fields */
  const patientDetails = {
    Name: formatApiScalar(od.patient_name),
    "Date of birth": formatApiScalar(od.date_of_birth),
    Gender: formatApiScalar(od.gender),
    "Height (cm)": formatApiScalar(od.height),
    "Weight (kg)": formatApiScalar(od.weight),
    Clinic: formatApiScalar(od.clinic_name),
    Mobile: formatApiScalar(od.mobile_no),
    Email: formatApiScalar(od.email),
    "Liner thickness": formatApiScalar(od.liner_thickness),
    "Design by": formatApiScalar(od.design_by),
    "Print by": formatApiScalar(od.print_by),
    "Finish type": formatApiScalar(od.finish_type),
  };

  /** Dashboard Assessment card — fixed field set / labels */
  const assessmentDetails = {
    "Foot Type": formatApiScalar(od.foot_type),
    "Amputation Side": formatApiScalar(od.amputated_leg),
    "Stump Type": formatApiScalar(od.stump_type),
    "Locking Type": formatApiScalar(od.locking_system ?? od.locking_type),
    "Flexion Angle (Deg)": formatApiScalar(od.flexion_angle),
    "Add/Abd Angle (Deg)": formatApiScalar(
      od.abductionadduction_angle ?? od.add_abd_angle ?? od.abduction_angle,
    ),
  };

  const keyMeasurements = {
    "Value A Stump Size (cm) *": formatApiScalar(od.stump_length),
    "Value B Patella to Ground (cm) *": formatApiScalar(od.stump_size),
    "Value C Circumference of Stump at 3 cm gap (cm)*":
      resolveValueCPrimaryMm(od),
  };

  const globalReduction = {};
  const designModificationRows = [];
  if (Array.isArray(od.socket_design_details)) {
    [...od.socket_design_details]
      .sort((a, b) => (a.idx ?? 0) - (b.idx ?? 0))
      .forEach((entry) => {
        const label = `${entry.area}. ${entry.area_name} (mm)`;
        const displayMm = resolveSocketDesignDisplayMm(entry);
        globalReduction[label] = displayMm;
        designModificationRows.push({
          label: `${entry.area}. ${entry.area_name}`,
          defaultMm: formatSignedValue(entry.default_mm),
          cpoInputMm: hasCpoInputMm(entry.cpo_input_mm)
            ? formatSignedValue(entry.cpo_input_mm)
            : null,
          displayMm,
        });
      });
  }

  const lockingSystemDetails = {
    "Locking System": formatApiScalar(od.locking_system ?? od.locking_type),
  };

  const footDetails = {
    Brand: formatApiScalar(
      firstDefined(
        pick(foot, "brand", "foot_brand", "make"),
        od.foot_brand,
        od.foot_make,
        od.prosthetic_foot_brand,
        od.brand,
      ),
    ),
    Model: formatApiScalar(
      firstDefined(
        pick(foot, "model", "foot_model", "model_name"),
        od.foot_model,
        od.model_name,
        od.prosthetic_foot_model,
        od.foot_model_name,
        od.model,
        od.item_code,
      ),
    ),
    "Activity Level": formatApiScalar(
      firstDefined(
        pick(foot, "activity_level", "k_level"),
        od.activity_level,
        od.foot_activity_level,
        od.k_level,
      ),
    ),
    Size: formatApiScalar(
      firstDefined(
        pick(foot, "size", "foot_size", "shoe_size"),
        od.foot_size,
        od.prosthetic_foot_size,
        od.foot_size_mm,
        od.shoe_size,
        od.size,
      ),
    ),
  };

  return {
    patientDetails,
    assessmentDetails,
    keyMeasurements,
    globalReduction,
    lockingSystemDetails,
    footDetails,
    designModificationRows,
    valueCircumferenceDetails: mapValueCircumferenceDetails(od),
    orderHeader: {
      sales_order: formatApiScalar(data.sales_order ?? od.sales_order_id),
      customer: formatApiScalar(data.customer ?? od.customer),
      order_type: formatApiScalar(data.order_type),
      doc_name: formatApiScalar(od.name),
      item_code: formatApiScalar(od.item_code),
    },
    amputatedLeg: od.amputated_leg || "",
  };
}

// API 3: Get scan/design files metadata for an order
export async function fetchScanFilesMeta(salesOrderId, customer) {
  const url = `${API_BASE}/method/bengaitlabserp.apis.order_types.bk_order.get_bk_design_files_meta`;
  const { data } = await erpApi.post(
    url,
    { sales_order_id: salesOrderId, customer },
    { headers: { "Content-Type": "application/json" } },
  );
  throwIfFrappeMessageError(data);
  return data?.message ?? data;
}

/**
 * Persist “scan final preview completed” for BK design (Frappe).
 * @param {{ salesOrderId: string, customer: string, fileKey?: string, fileName?: string, meshFormat?: string, contentSha256?: string, metadata?: Record<string, unknown> }} opts
 */
export async function recordBkDesignScanFinalPreviewComplete(opts) {
  const {
    salesOrderId,
    customer,
    fileKey,
    fileName,
    meshFormat,
    contentSha256,
    metadata,
  } = opts || {};
  const url = `${API_BASE}/method/bengaitlabserp.apis.order_types.bk_order.record_bk_design_scan_final_preview_complete`;
  const payload = {
    sales_order_id: salesOrderId,
    customer,
    ...(fileKey != null && fileKey !== "" ? { file_key: fileKey } : {}),
    ...(fileName != null && fileName !== "" ? { file_name: fileName } : {}),
    ...(meshFormat != null && meshFormat !== "" ? { mesh_format: meshFormat } : {}),
    ...(contentSha256 != null && contentSha256 !== ""
      ? { content_sha256: contentSha256 }
      : {}),
    ...(metadata != null && typeof metadata === "object" ? { metadata } : {}),
  };
  const { data } = await erpApi.post(url, payload, {
    headers: { "Content-Type": "application/json" },
  });
  throwIfFrappeMessageError(data);
  return data?.message ?? data;
}

/**
 * Scan final preview mesh — presigned PUT targets (PLY or STL).
 *
 * POST /method/bengaitlabserp.apis.order_types.bk_order.create_bk_design_scan_final_preview_presigned_uploads
 */
export async function createBkDesignScanFinalPreviewPresignedUploads({
  salesOrderId,
  customer,
  mode,
}) {
  const url = `${API_BASE}/method/bengaitlabserp.apis.order_types.bk_order.create_bk_design_scan_final_preview_presigned_uploads`;
  const payload = {
    sales_order_id: salesOrderId,
    customer,
    mode,
  };
  const { data } = await erpApi.post(url, payload, {
    headers: { "Content-Type": "application/json" },
  });
  throwIfFrappeMessageError(data);
  return data?.message ?? data;
}

/**
 * Finalize scan final preview direct upload after PUT(s) to R2.
 *
 * POST /method/bengaitlabserp.apis.order_types.bk_order.finalize_bk_design_scan_final_preview_direct_upload
 */
export async function finalizeBkDesignScanFinalPreviewDirectUpload({
  salesOrderId,
  customer,
  mode,
  mesh_key,
  metadata,
}) {
  const url = `${API_BASE}/method/bengaitlabserp.apis.order_types.bk_order.finalize_bk_design_scan_final_preview_direct_upload`;
  const payload = {
    sales_order_id: salesOrderId,
    customer,
    mode,
    mesh_key,
    ...(metadata != null ? { metadata } : {}),
  };
  const { data } = await erpApi.post(url, payload, {
    headers: { "Content-Type": "application/json" },
  });
  throwIfFrappeMessageError(data);
  return data?.message ?? data;
}

/**
 * Upload scan final preview checkpoint mesh (presign → PUT → finalize), same transport pattern as rectification.
 * @param {Blob|File} fileOrFiles — `.ply` (mode ply) or `.stl` (mode stl)
 */
export async function uploadScanFinalPreviewCheckpoint(
  salesOrderId,
  customer,
  fileOrFiles,
  metadata,
  contentSha256,
) {
  void contentSha256;
  const files = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles];
  const file = files?.[0];
  if (!(file instanceof Blob)) {
    throw new Error("Invalid file payload for scan final preview direct upload");
  }

  const name = file instanceof File ? file.name : "";
  const mode =
    String(name).toLowerCase().endsWith(".stl") ||
    (file.type && file.type.includes("stl"))
      ? "stl"
      : "ply";

  const presign = await createBkDesignScanFinalPreviewPresignedUploads({
    salesOrderId,
    customer,
    mode,
  });

  const uploadTargets = presign?.upload_targets;
  if (!Array.isArray(uploadTargets) || uploadTargets.length === 0) {
    throw new Error("Missing upload_targets for scan final preview presign response");
  }

  const meshTarget =
    uploadTargets.find((t) => t?.file_role === "mesh") ||
    uploadTargets.find((t) => t?.file_role === mode) ||
    uploadTargets[0];

  const extraTargets = uploadTargets.filter((t) => t !== meshTarget);
  if (extraTargets.length > 0) {
    throw new Error(
      `Scan final preview direct upload: expected single mesh target, got ${uploadTargets.length} targets`,
    );
  }

  await putToBkDirectUploadTarget(meshTarget, file);

  return finalizeBkDesignScanFinalPreviewDirectUpload({
    salesOrderId,
    customer,
    mode,
    mesh_key: meshTarget?.file_key,
    metadata,
  });
}

/**
 * Optional JSON sent with BK source uploads/finalize.
 * @typedef {{ schema_version?: number, preparing_scan?: { manual_scale_adjustment?: number, model_scale_unit?: string } }} BkSourceUploadMetadata
 */

/**
 * Create presigned URL for BK source direct upload.
 */
export async function createBkDesignSourcePresignedUploadUrl(
  salesOrderId,
  customer,
  fileName,
  contentType,
) {
  const url = `${API_BASE}/method/bengaitlabserp.apis.order_types.bk_order.create_bk_design_source_presigned_upload_url`;
  const payload = {
    sales_order_id: salesOrderId,
    customer,
    file_name: fileName,
    content_type: contentType,
  };
  const { data } = await erpApi.post(url, payload, {
    headers: { "Content-Type": "application/json" },
  });
  throwIfFrappeMessageError(data);
  return data?.message ?? data;
}

/**
 * Finalize BK source direct upload and optionally persist metadata.
 */
export async function finalizeBkDesignSourceDirectUpload(
  salesOrderId,
  customer,
  fileKey,
  metadata,
) {
  const url = `${API_BASE}/method/bengaitlabserp.apis.order_types.bk_order.finalize_bk_design_source_direct_upload`;
  const payload = {
    sales_order_id: salesOrderId,
    customer,
    file_key: fileKey,
    ...(metadata != null && typeof metadata === "object" ? { metadata } : {}),
  };
  const { data } = await erpApi.post(url, payload, {
    headers: { "Content-Type": "application/json" },
  });
  throwIfFrappeMessageError(data);
  return data?.message ?? data;
}

/**
 * Persist source metadata without uploading file bytes.
 * This keeps existing metadata-save behavior for preparing scan.
 */
export async function persistBkDesignSourceMetadata(
  salesOrderId,
  customer,
  metadata,
) {
  const fd = new FormData();
  fd.append("sales_order_id", salesOrderId);
  fd.append("customer", customer);
  if (metadata != null && typeof metadata === "object") {
    fd.append("metadata", JSON.stringify(metadata));
  }
  const url = `${API_BASE}/method/bengaitlabserp.apis.order_types.bk_order.upload_bk_design_source_files`;
  const { data } = await erpApi.post(url, fd);
  throwIfFrappeMessageError(data);
  return data?.message ?? data;
}

/**
 * Upload file bytes to a presigned R2 URL using the exact signed Content-Type.
 */
async function putToPresignedR2(uploadUrl, contentType, file) {
  const putRes = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": contentType },
    body: file,
  });
  if (!putRes.ok) {
    const text = await putRes.text().catch(() => "");
    throw new Error(
      `Direct R2 upload failed: ${putRes.status}${text ? ` ${text}` : ""}`,
    );
  }
}

/**
 * Upload one source file directly to R2 via presigned PUT, then finalize.
 * Returns finalize response.
 */
export async function uploadSourceFileDirectToR2(
  salesOrderId,
  customer,
  file,
  metadata,
) {
  if (!(file instanceof Blob)) {
    throw new Error("Invalid file payload for BK source upload");
  }
  const fileName =
    typeof file?.name === "string" && file.name.trim() !== ""
      ? file.name
      : "source_file";
  const contentType =
    typeof file?.type === "string" && file.type.trim() !== ""
      ? file.type
      : "application/octet-stream";
  const presign = await createBkDesignSourcePresignedUploadUrl(
    salesOrderId,
    customer,
    fileName,
    contentType,
  );
  const uploadUrl = presign?.upload_url;
  const fileKey = presign?.file_key;
  if (!uploadUrl || !fileKey) {
    throw new Error("Presigned upload URL response missing upload_url/file_key");
  }
  const putContentType =
    (typeof presign?.recommended_content_type === "string" &&
      presign.recommended_content_type.trim() !== ""
      ? presign.recommended_content_type
      : null) ||
    presign?.headers?.["Content-Type"] ||
    presign?.headers?.["content-type"] ||
    contentType;
  await putToPresignedR2(uploadUrl, putContentType, file);
  return finalizeBkDesignSourceDirectUpload(
    salesOrderId,
    customer,
    fileKey,
    metadata,
  );
}

/**
 * Upload source/scan files for an order using direct-to-R2 flow.
 * @param {string} salesOrderId
 * @param {string} customer
 * @param {File[]} files
 * @param {BkSourceUploadMetadata} [metadata]
 */
export async function uploadSourceFiles(
  salesOrderId,
  customer,
  files,
  metadata,
) {
  const list = Array.isArray(files) ? files : [];
  if (list.length === 0) {
    return persistBkDesignSourceMetadata(salesOrderId, customer, metadata);
  }
  const out = [];
  for (const file of list) {
    out.push(
      await uploadSourceFileDirectToR2(
        salesOrderId,
        customer,
        file,
        metadata,
      ),
    );
  }
  return out.length === 1 ? out[0] : out;
}

// API 2: Upload final design STL for an order
export async function uploadFinalStl(salesOrderId, customer, file, version) {
  if (!(file instanceof Blob)) {
    throw new Error("Invalid file payload for final STL direct upload");
  }

  const presign = await createBkDesignFinalStlPresignedUploadUrl({
    salesOrderId,
    customer,
    version,
  });

  const uploadUrl = presign?.upload_url;
  const fileKey = presign?.file_key;
  if (!uploadUrl || !fileKey) {
    throw new Error("Missing upload_url/file_key for final STL presign response");
  }

  await putToPresignedR2UploadUrl(uploadUrl, file);

  const finalizeRes = await finalizeBkDesignFinalStlDirectUpload({
    salesOrderId,
    customer,
    file_key: fileKey,
  });

  return finalizeRes;
}

/**
 * Upload rectification checkpoint mesh file(s) (multipart). See docs/integartion-guide.md §1.
 * @param {string} salesOrderId
 * @param {string} customer
 * @param {File|Blob|Array<File|Blob>} fileOrFiles — typically one `.ply` checkpoint file
 * @param {Record<string, unknown>} [metadata] — JSON object; sent as JSON string field `metadata`
 * @param {string} [contentSha256] — lowercase hex SHA-256 of file bytes (optional integrity check)
 */
export async function uploadRectificationCheckpoint(
  salesOrderId,
  customer,
  fileOrFiles,
  metadata,
  contentSha256,
) {
  void contentSha256;
  return uploadBkDesignStepCheckpoint({
    salesOrderId,
    customer,
    step: "rectification",
    mode: "ply",
    file: fileOrFiles,
    metadata,
  });
}

/**
 * Upload valve checkpoint mesh file. Client sends ASCII PLY (vertex colors when
 * present), same clinical format as the rectification checkpoint, under a
 * dedicated `valve` step key.
 */
export async function uploadValveCheckpoint(
  salesOrderId,
  customer,
  fileOrFiles,
  metadata,
  contentSha256,
) {
  void contentSha256;
  return uploadBkDesignStepCheckpoint({
    salesOrderId,
    customer,
    step: "valve",
    mode: "ply",
    file: fileOrFiles,
    metadata,
  });
}

/**
 * Upload locking checkpoint mesh file(s). Client sends binary STL for locking checkpoints.
 */
export async function uploadLockingCheckpoint(
  salesOrderId,
  customer,
  fileOrFiles,
  metadata,
  contentSha256,
) {
  // Backwards-compatible wrapper: when old code calls `uploadLockingCheckpoint`
  // without a variant, treat it as `plain`.
  return uploadLockingCheckpointVariant(
    salesOrderId,
    customer,
    fileOrFiles,
    "plain",
    metadata,
    contentSha256,
  );
}

/**
 * Create presigned upload targets for BK rectification/locking checkpoint direct uploads.
 *
 * Presign endpoint:
 * POST /method/bengaitlabserp.apis.order_types.bk_order.create_bk_design_checkpoint_presigned_uploads
 */
export async function createBkDesignCheckpointPresignedUploads({
  salesOrderId,
  customer,
  step,
  mode,
  variant,
  textures,
}) {
  const url = `${API_BASE}/method/bengaitlabserp.apis.order_types.bk_order.create_bk_design_checkpoint_presigned_uploads`;
  const payload = {
    sales_order_id: salesOrderId,
    customer,
    step,
    mode,
  };
  if (variant != null && variant !== "") payload.variant = variant;
  if (Array.isArray(textures) && textures.length > 0) payload.textures = textures;

  const { data } = await erpApi.post(url, payload, {
    headers: { "Content-Type": "application/json" },
  });
  throwIfFrappeMessageError(data);
  return data?.message ?? data;
}

/**
 * Finalize BK checkpoint direct upload.
 *
 * Finalize endpoint:
 * POST /method/bengaitlabserp.apis.order_types.bk_order.finalize_bk_design_checkpoint_direct_upload
 */
export async function finalizeBkDesignCheckpointDirectUpload({
  salesOrderId,
  customer,
  step,
  mode,
  variant,
  mesh_key,
  obj_key,
  mtl_key,
  texture_keys,
  metadata,
}) {
  const url = `${API_BASE}/method/bengaitlabserp.apis.order_types.bk_order.finalize_bk_design_checkpoint_direct_upload`;
  const payload = {
    sales_order_id: salesOrderId,
    customer,
    step,
    mode,
    ...(variant != null && variant !== "" ? { variant } : {}),
    ...(mesh_key ? { mesh_key } : {}),
    ...(obj_key ? { obj_key } : {}),
    ...(mtl_key ? { mtl_key } : {}),
    ...(Array.isArray(texture_keys) ? { texture_keys } : {}),
    ...(metadata != null ? { metadata } : {}),
  };

  const { data } = await erpApi.post(url, payload, {
    headers: { "Content-Type": "application/json" },
  });
  throwIfFrappeMessageError(data);
  return data?.message ?? data;
}

/**
 * Generic per-step checkpoint upload: presign → PUT → finalize.
 *
 * All step checkpoints share one transport; only the `step` slot, `mode`
 * (ply/stl) and optional `variant` differ. The step-specific wrappers below
 * (`uploadRectificationCheckpoint`, `uploadLockingCheckpointVariant`, …) are
 * thin aliases kept for existing callers.
 *
 * @param {object} args
 * @param {string} args.salesOrderId
 * @param {string} args.customer
 * @param {string} args.step - server slot key, e.g. `rectification`, `trimline`
 * @param {'ply'|'stl'} args.mode
 * @param {string} [args.variant] - e.g. locking `plain` | `bulldog`
 * @param {File|Blob|Array<File|Blob>} args.file
 * @param {Record<string, unknown>} [args.metadata]
 */
export async function uploadBkDesignStepCheckpoint({
  salesOrderId,
  customer,
  step,
  mode,
  variant,
  file: fileOrFiles,
  metadata,
}) {
  const files = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles];
  const file = files?.[0];
  if (!(file instanceof Blob)) {
    throw new Error(`Invalid file payload for ${step} checkpoint direct upload`);
  }

  const presign = await createBkDesignCheckpointPresignedUploads({
    salesOrderId,
    customer,
    step,
    mode,
    variant,
  });

  const uploadTargets = presign?.upload_targets;
  if (!Array.isArray(uploadTargets) || uploadTargets.length === 0) {
    throw new Error(`Missing upload_targets for ${step} checkpoint presign response`);
  }

  const meshTarget =
    uploadTargets.find((t) => t?.file_role === "mesh") ||
    uploadTargets.find((t) => t?.file_role === mode) ||
    uploadTargets[0];

  // Single-mesh modes only: if backend hands back extra roles (OBJ + MTL +
  // textures) we have no bytes to satisfy them, so fail loudly instead of
  // finalizing a half-written checkpoint.
  if (uploadTargets.length > 1) {
    throw new Error(
      `${step} checkpoint direct upload: expected single mesh target, got ${uploadTargets.length} targets`,
    );
  }

  await putToBkDirectUploadTarget(meshTarget, file);

  return finalizeBkDesignCheckpointDirectUpload({
    salesOrderId,
    customer,
    step,
    mode,
    variant,
    mesh_key: meshTarget?.file_key,
    metadata,
  });
}

async function putToBkDirectUploadTarget(target, blob) {
  if (!target?.upload_url) {
    throw new Error("Direct upload target missing upload_url");
  }
  const putRes = await fetch(target.upload_url, {
    method: "PUT",
    headers: { "Content-Type": "application/octet-stream" },
    body: blob,
  });
  if (!putRes.ok) {
    const text = await putRes.text().catch(() => "");
    throw new Error(`Direct R2 upload failed: ${putRes.status}${text ? ` ${text}` : ""}`);
  }
}

async function putToPresignedR2UploadUrl(uploadUrl, blob) {
  const putRes = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": "application/octet-stream" },
    body: blob,
  });
  if (!putRes.ok) {
    const text = await putRes.text().catch(() => "");
    throw new Error(
      `Direct R2 upload failed (${putRes.status})${text ? ` ${text}` : ""}`,
    );
  }
}

/**
 * Presign request for final STL direct upload.
 *
 * POST /method/bengaitlabserp.apis.order_types.bk_order.create_bk_design_final_stl_presigned_upload_url
 */
export async function createBkDesignFinalStlPresignedUploadUrl({
  salesOrderId,
  customer,
  version,
}) {
  const url = `${API_BASE}/method/bengaitlabserp.apis.order_types.bk_order.create_bk_design_final_stl_presigned_upload_url`;
  const payload = {
    sales_order_id: salesOrderId,
    customer,
  };
  if (version != null && version !== "") payload.version = String(version);

  const { data } = await erpApi.post(url, payload, {
    headers: { "Content-Type": "application/json" },
  });
  throwIfFrappeMessageError(data);
  return data?.message ?? data;
}

/**
 * Finalize final STL direct upload.
 *
 * POST /method/bengaitlabserp.apis.order_types.bk_order.finalize_bk_design_final_stl_direct_upload
 */
export async function finalizeBkDesignFinalStlDirectUpload({
  salesOrderId,
  customer,
  file_key,
}) {
  const url = `${API_BASE}/method/bengaitlabserp.apis.order_types.bk_order.finalize_bk_design_final_stl_direct_upload`;
  const payload = {
    sales_order_id: salesOrderId,
    customer,
    file_key,
  };

  const { data } = await erpApi.post(url, payload, {
    headers: { "Content-Type": "application/json" },
  });
  throwIfFrappeMessageError(data);
  return data?.message ?? data;
}

// ---------------------------------------------------------------------------
// Cross-device undo material
//
// Pre-apply ("before") socket blobs and the per-step undo manifest. These four
// endpoints are NOT yet implemented server-side — see `bengaitlabserp/docs/undo-api.md`
// for the contract they must satisfy. Until they ship, `isUndoApiUnavailable`
// lets the caller detect their absence once and fall back to device-local undo
// for the rest of the session, exactly as `stepCheckpointUpload` does for
// checkpoint slots the backend does not know yet.
// ---------------------------------------------------------------------------

const UNDO_METHOD_PREFIX = "bengaitlabserp.apis.order_types.bk_order";

/**
 * True when a request failed because the endpoint does not exist on this server
 * (rather than because the call itself was bad). Frappe answers an unknown
 * method with 404 / "Method Not Found"; a reverse proxy may answer 501.
 *
 * @param {unknown} err
 */
export function isUndoApiUnavailable(err) {
  const status = err?.response?.status ?? err?.frappeStatusCode;
  if (status === 404 || status === 501) return true;
  const text = String(err?.message ?? "").toLowerCase();
  return (
    text.includes("method not found") ||
    text.includes("not whitelisted") ||
    text.includes("does not exist")
  );
}

/**
 * Presign a PUT for one before-mesh blob.
 *
 * POST /method/…bk_order.create_bk_design_undo_presigned_upload
 * @returns {Promise<{ upload_url: string, file_key: string }>}
 */
export async function createBkDesignUndoPresignedUpload({
  salesOrderId,
  customer,
  step,
  toolId,
  bytes,
}) {
  const url = `${API_BASE}/method/${UNDO_METHOD_PREFIX}.create_bk_design_undo_presigned_upload`;
  const { data } = await erpApi.post(
    url,
    {
      sales_order_id: salesOrderId,
      customer,
      step,
      tool_id: toolId,
      ...(Number.isFinite(bytes) ? { bytes } : {}),
    },
    { headers: { "Content-Type": "application/json" } },
  );
  throwIfFrappeMessageError(data);
  return data?.message ?? data;
}

/**
 * Commit a before-mesh blob after its PUT succeeded, and merge the step's undo
 * manifest server-side.
 *
 * POST /method/…bk_order.finalize_bk_design_undo_upload
 */
export async function finalizeBkDesignUndoUpload({
  salesOrderId,
  customer,
  step,
  toolId,
  file_key,
  metadata,
}) {
  const url = `${API_BASE}/method/${UNDO_METHOD_PREFIX}.finalize_bk_design_undo_upload`;
  const { data } = await erpApi.post(
    url,
    {
      sales_order_id: salesOrderId,
      customer,
      step,
      tool_id: toolId,
      file_key,
      ...(metadata != null ? { metadata } : {}),
    },
    { headers: { "Content-Type": "application/json" } },
  );
  throwIfFrappeMessageError(data);
  return data?.message ?? data;
}

/**
 * Fetch every step manifest plus the download URLs for the stored before-meshes.
 *
 * POST /method/…bk_order.get_bk_design_undo_manifest
 * @returns {Promise<{ steps: Record<string, object>, blobs: Array<object> }>}
 */
export async function getBkDesignUndoManifest({ salesOrderId, customer }) {
  const url = `${API_BASE}/method/${UNDO_METHOD_PREFIX}.get_bk_design_undo_manifest`;
  const { data } = await erpApi.post(
    url,
    { sales_order_id: salesOrderId, customer },
    { headers: { "Content-Type": "application/json" } },
  );
  throwIfFrappeMessageError(data);
  return data?.message ?? data;
}

/**
 * Delete undo material server-side.
 *
 * Pass `steps` to drop specific steps (backward revert), or `retainSteps` to keep
 * only those and drop everything else for the order (post-export stage pruning).
 *
 * POST /method/…bk_order.delete_bk_design_undo_blobs
 */
export async function deleteBkDesignUndoBlobs({
  salesOrderId,
  customer,
  steps,
  retainSteps,
  toolIds,
}) {
  const url = `${API_BASE}/method/${UNDO_METHOD_PREFIX}.delete_bk_design_undo_blobs`;
  const { data } = await erpApi.post(
    url,
    {
      sales_order_id: salesOrderId,
      customer,
      ...(Array.isArray(steps) ? { steps } : {}),
      ...(Array.isArray(retainSteps) ? { retain_steps: retainSteps } : {}),
      ...(Array.isArray(toolIds) ? { tool_ids: toolIds } : {}),
    },
    { headers: { "Content-Type": "application/json" } },
  );
  throwIfFrappeMessageError(data);
  return data?.message ?? data;
}

/** Upload raw bytes to a presigned R2 PUT target (shared with checkpoint uploads). */
export async function putBytesToPresignedUrl(uploadUrl, blob) {
  return putToPresignedR2UploadUrl(uploadUrl, blob);
}
